/* ===== Campaigns Page ===== */

import { useEffect, useState } from 'react'
import type { Campaign } from '@zetyar/domain'
import type { PendingContribution } from '@zetyar/data-contracts'
import { CircleCheck, CircleX, HeartHandshake, Plus, Trash2 } from 'lucide-react'
import { useApp } from '@/presentation/providers/useApp'
import { Card, CardContent, CardHeader, CardTitle } from '@/presentation/components/ui/Card'
import { Button } from '@/presentation/components/ui/Button'
import { Badge } from '@/presentation/components/ui/Badge'
import { Input } from '@/presentation/components/ui/Input'
import { Modal } from '@/presentation/components/ui/Modal'
import { createWebTenantContext } from '@/infrastructure/repositories/announcements'
import { webCampaignRepository } from '@/infrastructure/repositories/campaigns'
import { useReauthGuard } from '@/presentation/hooks/useReauthGuard'

const STATUS_LABEL: Record<string, string> = {
  draft: 'Borrador',
  active: 'Activa',
  paused: 'Pausada',
  completed: 'Completada',
  cancelled: 'Cancelada',
}

const STATUS_VARIANT: Record<string, 'neutral' | 'success' | 'danger'> = {
  draft: 'neutral',
  active: 'success',
  paused: 'neutral',
  completed: 'success',
  cancelled: 'danger',
}

function formatAmount(amount: number, currency: string): string {
  return new Intl.NumberFormat('es-CL', { style: 'currency', currency, maximumFractionDigits: 0 }).format(amount)
}

export function CampaignsPage() {
  const { currentMembership, currentOrganization, profile, user, permissions } = useApp()
  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [contributeTarget, setContributeTarget] = useState<Campaign | null>(null)
  const [amount, setAmount] = useState('')
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [goalAmount, setGoalAmount] = useState('')

  const canManage = profile?.isSuperAdmin || permissions.includes('campaigns.manage')
  const canConfirmPayments =
    profile?.isSuperAdmin ||
    currentMembership?.roles?.includes('organization_admin') ||
    currentMembership?.roles?.includes('treasurer')
  const [pendingContributions, setPendingContributions] = useState<PendingContribution[]>([])
  const [isLoadingPending, setIsLoadingPending] = useState(false)
  const [decidingContributionId, setDecidingContributionId] = useState<string | null>(null)
  const { guard, reauthModal } = useReauthGuard()

  const tenantContext = () => {
    if (!currentOrganization || !profile || !user) return null
    return createWebTenantContext({
      authUid: user.uid,
      personId: profile.id,
      organizationId: currentOrganization.id,
      membershipId: currentMembership?.id,
    })
  }

  const load = async () => {
    const context = tenantContext()
    if (!context) {
      setCampaigns([])
      setIsLoading(false)
      return
    }

    try {
      setIsLoading(true)
      const result = await webCampaignRepository.listVisible(context, { limit: 100 })
      setCampaigns(result)
    } catch (error) {
      console.error('Error al cargar campañas:', error)
      setCampaigns([])
    } finally {
      setIsLoading(false)
    }
  }

  const loadPending = async () => {
    const context = tenantContext()
    if (!context || !canConfirmPayments || !webCampaignRepository.listPendingContributions) {
      setPendingContributions([])
      return
    }

    try {
      setIsLoadingPending(true)
      const result = await webCampaignRepository.listPendingContributions(context, { limit: 100 })
      setPendingContributions(result)
    } catch (error) {
      console.error('Error al cargar aportes pendientes:', error)
      setPendingContributions([])
    } finally {
      setIsLoadingPending(false)
    }
  }

  useEffect(() => {
    let isMounted = true
    const run = async () => {
      if (!isMounted) return
      await load()
      await loadPending()
    }
    void run()
    return () => {
      isMounted = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMembership, currentOrganization, profile, user])

  const handleDecideContribution = async (contribution: PendingContribution, decision: 'confirm' | 'reject') => {
    const context = tenantContext()
    const action = decision === 'confirm' ? webCampaignRepository.confirmContribution : webCampaignRepository.rejectContribution
    if (!context || !action) return

    try {
      setDecidingContributionId(contribution.id)
      await action(context, contribution)
      await load()
      await loadPending()
    } catch (error) {
      console.error('Error al decidir sobre el aporte:', error)
    } finally {
      setDecidingContributionId(null)
    }
  }

  const handleContribute = async (e: React.FormEvent) => {
    e.preventDefault()
    const context = tenantContext()
    if (!context || !contributeTarget || !webCampaignRepository.contribute) return

    const amountValue = Number(amount)
    if (!Number.isFinite(amountValue) || amountValue <= 0) return

    try {
      const { initPoint } = await webCampaignRepository.contribute(
        context,
        contributeTarget.id,
        amountValue,
        false,
        undefined,
        profile?.email,
      )
      window.location.href = initPoint
      return
    } catch (error) {
      console.error('Error al aportar a la campaña:', error)
      alert(error instanceof Error ? error.message : 'No se pudo iniciar el pago del aporte.')
    }

    setContributeTarget(null)
    setAmount('')
  }

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    const context = tenantContext()
    if (!context || !webCampaignRepository.create) return

    const goalValue = Number(goalAmount)
    if (!Number.isFinite(goalValue) || goalValue <= 0) return

    try {
      await webCampaignRepository.create(context, { title, description, goalAmount: goalValue })
      await load()
    } catch (error) {
      console.error('Error al crear la campaña:', error)
    }

    setIsCreateOpen(false)
    setTitle('')
    setDescription('')
    setGoalAmount('')
  }

  const handleStatusChange = async (campaignId: string, status: Campaign['status']) => {
    const context = tenantContext()
    if (!context || !webCampaignRepository.updateStatus) return

    try {
      await webCampaignRepository.updateStatus(context, campaignId, status)
      await load()
    } catch (error) {
      console.error('Error al cambiar el estado de la campaña:', error)
    }
  }

  const handleDelete = (campaignId: string) => {
    guard(async () => {
      const context = tenantContext()
      if (!context || !webCampaignRepository.delete) return

      try {
        await webCampaignRepository.delete(context, campaignId)
        await load()
      } catch (error) {
        console.error('Error al eliminar la campaña:', error)
      }
    })
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 tracking-tight">
            Campañas y fondos
          </h1>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
            Recaudacion transparente para fondos colectivos en {currentOrganization?.name}
          </p>
        </div>
        {canManage && (
          <Button variant="primary" leftIcon={<Plus className="w-4 h-4" />} onClick={() => setIsCreateOpen(true)}>
            Nueva campaña
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <HeartHandshake className="w-5 h-5 text-emerald-600" />
            Campañas
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-sm text-neutral-500 dark:text-neutral-400 animate-pulse">Cargando...</p>
          ) : campaigns.length === 0 ? (
            <p className="text-sm text-neutral-500 dark:text-neutral-400">No hay campañas activas.</p>
          ) : (
            <div className="space-y-4">
              {campaigns.map((campaign) => (
                <div
                  key={campaign.id}
                  className="p-4 rounded-md border border-neutral-200 dark:border-neutral-800 space-y-3"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="font-semibold text-neutral-900 dark:text-neutral-100">{campaign.title}</h3>
                      <p className="text-sm text-neutral-600 dark:text-neutral-300 mt-1">{campaign.description}</p>
                    </div>
                    <Badge variant={STATUS_VARIANT[campaign.status]}>{STATUS_LABEL[campaign.status]}</Badge>
                  </div>

                  <div className="space-y-1">
                    <div className="h-2 rounded-full bg-neutral-100 dark:bg-neutral-800 overflow-hidden">
                      <div
                        className="h-full bg-emerald-500"
                        style={{ width: `${campaign.progressPercentage ?? 0}%` }}
                      />
                    </div>
                    <div className="flex items-center justify-between text-xs text-neutral-500 dark:text-neutral-400">
                      <span>
                        {formatAmount(campaign.raisedAmount, campaign.currency)} de{' '}
                        {formatAmount(campaign.goalAmount, campaign.currency)}
                      </span>
                      <span>{campaign.contributorCount} aportantes</span>
                    </div>
                  </div>

                  <div className="pt-2 border-t border-neutral-100 dark:border-neutral-800 flex items-center justify-end gap-2">
                    {canManage && campaign.status === 'active' && (
                      <Button size="sm" variant="outline" onClick={() => void handleStatusChange(campaign.id, 'paused')}>
                        Pausar
                      </Button>
                    )}
                    {canManage && campaign.status === 'paused' && (
                      <Button size="sm" variant="outline" onClick={() => void handleStatusChange(campaign.id, 'active')}>
                        Reactivar
                      </Button>
                    )}
                    {campaign.status === 'active' && (
                      <Button size="sm" variant="primary" onClick={() => setContributeTarget(campaign)}>
                        Aportar
                      </Button>
                    )}
                    {canManage && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 w-8 p-0 flex items-center justify-center"
                        aria-label="Eliminar campaña"
                        onClick={() => handleDelete(campaign.id)}
                      >
                        <Trash2 className="w-3.5 h-3.5 text-danger-600" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {canConfirmPayments && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Aportes pendientes de confirmar</CardTitle>
            <p className="text-sm text-neutral-500 dark:text-neutral-400">
              No hay pasarela de pago conectada: cada aporte queda pendiente hasta que un administrador o
              tesorero confirme que el dinero se recibio realmente. Quien realizo el aporte no puede
              confirmarlo.
            </p>
          </CardHeader>
          <CardContent>
            {isLoadingPending ? (
              <p className="text-sm text-neutral-500 dark:text-neutral-400 animate-pulse">Cargando...</p>
            ) : pendingContributions.length === 0 ? (
              <p className="text-sm text-neutral-500 dark:text-neutral-400">No hay aportes pendientes.</p>
            ) : (
              <div className="space-y-3">
                {pendingContributions.map((contribution) => (
                  <div
                    key={contribution.id}
                    className="flex items-center justify-between gap-4 p-3 rounded-md border border-neutral-200 dark:border-neutral-800"
                  >
                    <div>
                      <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                        {formatAmount(contribution.amount, contribution.currency)} - {contribution.campaignTitle}
                      </p>
                      <p className="text-xs text-neutral-500 dark:text-neutral-400">
                        {contribution.isAnonymous
                          ? 'Aportante anonimo'
                          : (contribution.contributorName ?? 'Aportante')}
                        {contribution.message ? ` - "${contribution.message}"` : ''}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={decidingContributionId === contribution.id}
                        onClick={() => void handleDecideContribution(contribution, 'reject')}
                        leftIcon={<CircleX className="w-4 h-4" />}
                      >
                        Rechazar
                      </Button>
                      <Button
                        size="sm"
                        variant="primary"
                        disabled={decidingContributionId === contribution.id}
                        onClick={() => void handleDecideContribution(contribution, 'confirm')}
                        leftIcon={<CircleCheck className="w-4 h-4" />}
                      >
                        Confirmar
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Modal isOpen={contributeTarget !== null} onClose={() => setContributeTarget(null)} title="Aportar a la campaña">
        <form onSubmit={handleContribute} className="space-y-4">
          <p className="text-sm text-neutral-700 dark:text-neutral-300">{contributeTarget?.title}</p>
          <p className="text-xs text-neutral-500 dark:text-neutral-400">
            No hay pasarela de pago conectada todavia. El aporte queda registrado como pendiente hasta que
            un administrador o tesorero confirme que el dinero se recibio.
          </p>
          <Input
            label="Monto (CLP)"
            type="number"
            min="1"
            step="1"
            required
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
          <div className="flex justify-end gap-3 pt-4 border-t border-neutral-100 dark:border-neutral-800">
            <Button variant="outline" type="button" onClick={() => setContributeTarget(null)}>
              Cancelar
            </Button>
            <Button variant="primary" type="submit">
              Registrar aporte
            </Button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={isCreateOpen} onClose={() => setIsCreateOpen(false)} title="Nueva campaña">
        <form onSubmit={handleCreate} className="space-y-4">
          <Input
            label="Titulo"
            placeholder="Ej: Fondo de emergencia"
            required
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <Input
            label="Descripcion"
            placeholder="Ej: Aportes para gastos imprevistos de la comunidad"
            required
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <Input
            label="Meta de recaudacion (CLP)"
            type="number"
            min="1"
            step="1"
            required
            value={goalAmount}
            onChange={(e) => setGoalAmount(e.target.value)}
          />
          <div className="flex justify-end gap-3 pt-4 border-t border-neutral-100 dark:border-neutral-800">
            <Button variant="outline" type="button" onClick={() => setIsCreateOpen(false)}>
              Cancelar
            </Button>
            <Button variant="primary" type="submit">
              Publicar campaña
            </Button>
          </div>
        </form>
      </Modal>
      {reauthModal}
    </div>
  )
}
